import React, { useCallback, useRef, useState } from "react";
import { postJson } from "../../lib/http";
import { ArrowRight, Lock, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useFormTimer, antispamErrorMessage } from "../../lib/antispam";
import { toast } from "../../lib/toast";
import { QButton } from "./QButton";
import { API_BASE } from "../../lib/apiBase";
import { deriveSource, buildSourceUrl, buildSourcePath, getReferrer } from "../../lib/leadSource";
import { getAttribution } from "../../lib/attribution";
import { inputCls, selectCls, textareaCls } from "../../lib/forms";

const API = API_BASE;

const INTERESTS = [
  { value: "solar_panels", label: "Solar panels" },
  { value: "battery_storage", label: "Battery storage" },
  { value: "solar_battery", label: "Solar + battery" },
  { value: "not_sure", label: "Not sure yet" },
];

const initialState = {
  name: "",
  email: "",
  phone: "",
  suburb: "",
  interest: "solar_panels",
  message: "",
  website: "", // honeypot
};

const Field = ({ label, htmlFor, error, children, optional }) => (  <div className="flex flex-col gap-1.5">
    <label
      htmlFor={htmlFor}
      className="text-[12.5px] font-medium text-ink flex items-center gap-1.5"
    >
      {label}
      {optional && <span className="text-subtle font-normal">(optional)</span>}
    </label>
    {children}
    {error && (
      <span
        className="text-[12px] text-red-600"
        data-testid={`${htmlFor}-error`}
      >
        {error}
      </span>
    )}
  </div>
);

export const QuoteForm = ({ source: sourceProp, testIdPrefix = "quote" }) => {
  const [form, setForm] = useState(initialState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const formTimer = useFormTimer();
  const navigate = useNavigate();
  // Phase 15 — formRef lets us scroll the form back into view on any error
  // path so mobile users on FB in-app browsers don't miss the failure
  // notification when their keyboard is open.
  const formRef = useRef(null);

  const handle = (key) => (e) =>
    setForm((s) => ({ ...s, [key]: e.target.value }));

  // Phase 15 — surface every form-level failure through BOTH a sonner toast
  // (8s duration so users actually see it) AND the inline `_form` error
  // (fallback for anyone who dismisses the toast), then scroll the form
  // into center view. The pre-existing tiny inline-only error was being
  // occluded by mobile keyboards on /facebook-landing.
  const surfaceFormError = useCallback((message) => {
    setErrors({ _form: message });
    toast.error(message, { duration: 8000 });
    if (formRef.current && typeof formRef.current.scrollIntoView === "function") {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  }, []);

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Please enter your full name.";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      e.email = "Please enter a valid email.";
    if (form.phone.replace(/\D/g, "").length < 6)
      e.phone = "Please enter a valid phone number.";
    if (form.suburb.trim().length < 2)
      e.suburb = "Please enter your suburb or postcode.";
    if (!form.interest) e.interest = "Please pick an option.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (submitting) return;

    // Phase 17 (GTM) — push a dataLayer click event AT THE TOP of the handler,
    // before validation / anti-spam / axios. This fires even when the submit is
    // subsequently rejected by validation or by the anti-spam guard, so GTM triggers on
    // this event count "submission attempts". The success event fires later,
    // only when the backend confirms the lead saved.
    if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
      const derivedSourceForEvent = sourceProp || deriveSource();
      window.dataLayer.push({
        event: "quote_form_submit_click",
        form_id: "quote_form",
        form_location: window.location.pathname,
        form_source: derivedSourceForEvent,
      });
    }

    if (!validate()) return;

    setSubmitting(true);
    try {
      await formTimer.settle();
      // Phase 12 — derive source from pathname unless the parent surface
      // explicitly overrides via the `source` prop (e.g. Contact passes
      // `source="contact_page"`). Home page omits the prop, so on
      // /facebook-landing the pathname-based derivation gives us
      // "facebook_landing" instead of the legacy "hero_form" default.
      // `source_url` is ALWAYS pathname+search so utm_* attribution
      // tags reach the team inbox.
      const derivedSource = deriveSource();
      const effectiveSource = sourceProp || derivedSource;
      const source_url = buildSourceUrl();
      const source_path = buildSourcePath();
      const referrer = getReferrer();
      const { data } = await postJson(
        `${API}/quote`,
        // Phase 37 - gclid / utm / landing page captured on arrival, so a
        // Google Ads click survives navigation to this form.
        { ...form, source: effectiveSource, source_url, source_path, referrer, ...getAttribution(), ...formTimer.fields() },
        { timeout: 15000 }
      );
      if (data && data.ok) {
        // Phase 17 (GTM) — push a dataLayer success event BEFORE navigating
        // away. Ordering matters: fire the event synchronously so GTM's tag
        // manager captures it before React unmounts this component.
        if (typeof window !== "undefined" && Array.isArray(window.dataLayer)) {
          window.dataLayer.push({
            event: "quote_form_submit_success",
            form_id: "quote_form",
            form_location: window.location.pathname,
            form_source: effectiveSource,
            lead_id: data.id || null,
          });
        }
        // Phase 17b (Google Ads) — fire the AW-622264121 conversion label
        // directly via gtag alongside the GTM dataLayer event. Belt-and-braces:
        // the GTM dataLayer trigger can be lost if a tag manager stalls or the
        // page navigates away before GTM initialises. `transaction_id` is
        // sourced from the lead id we just created, so Google Ads de-dupes
        // if this same conversion also fires from the ThankYou on-mount path.
        try {
          if (typeof window !== "undefined" && typeof window.gtag === "function") {
            window.gtag("event", "conversion", {
              send_to: "AW-622264121/kcQgCKfel9AcELn-26gC",
              value: 1.0,
              currency: "AUD",
              transaction_id: data.id || null,
            });
          }
        } catch (_) { /* ignore — never block navigation on tracking */ }
        const firstName = form.name.trim().split(/\s+/)[0];
        setForm(initialState);
        navigate("/thank-you", {
          state: { firstName, source: effectiveSource, id: data.id },
        });
        return;
      }
      // Phase 15 — defensive close on the silent-success-skip. Should never
      // fire in practice (backend always returns {ok:true} on 2xx) but if
      // the backend ever drifts the user won't be left staring at a button
      // that "did nothing".
      surfaceFormError(
        "Something unexpected happened. Please try again or call us directly."
      );
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const antispamMsg = antispamErrorMessage(detail);
      if (antispamMsg) {
        surfaceFormError(antispamMsg);
      } else if (Array.isArray(detail)) {
        const next = {};
        detail.forEach((d) => {
          const loc = (d.loc || []).slice(-1)[0];
          if (loc) next[loc] = d.msg;
        });
        setErrors(next);
        toast.error("Please fix the highlighted fields and try again.", {
          duration: 8000,
        });
        if (formRef.current && typeof formRef.current.scrollIntoView === "function") {
          formRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      } else {
        surfaceFormError(
          "Something went wrong sending your request. Please try again or call us."
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      ref={formRef}
      className="relative bg-white border border-border rounded-xl p-7 md:p-8 shadow-sm"
      data-testid={`${testIdPrefix}-form`}
      id="quote-form"
    >
      <div className="qv-eyebrow text-brand-orange mb-3 inline-flex items-center gap-1.5">
        <Sparkles className="h-3.5 w-3.5" strokeWidth={1.75} /> Free quote
      </div>
      <h3 className="qv-h3" data-testid={`${testIdPrefix}-form-headline`}>
        Get your no-obligation solar quote.
      </h3>
      <p className="text-[14px] text-subtle mt-2">
        We'll get back to you within one business day.
      </p>

      <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Field label="Full name" htmlFor="qf-name" error={errors.name}>
          <input
            id="qf-name"
            data-testid="quote-name"
            type="text"
            autoComplete="name"
            className={inputCls}
            value={form.name}
            onChange={handle("name")}
            placeholder="Jane Smith"
            required
          />
        </Field>
        <Field label="Email" htmlFor="qf-email" error={errors.email}>
          <input
            id="qf-email"
            data-testid="quote-email"
            type="email"
            autoComplete="email"
            className={inputCls}
            value={form.email}
            onChange={handle("email")}
            placeholder="you@example.com"
            required
          />
        </Field>
        <Field label="Phone" htmlFor="qf-phone" error={errors.phone}>
          <input
            id="qf-phone"
            data-testid="quote-phone"
            type="tel"
            autoComplete="tel"
            className={inputCls}
            value={form.phone}
            onChange={handle("phone")}
            placeholder="0400 123 456"
            required
          />
        </Field>
        <Field label="Suburb / postcode" htmlFor="qf-suburb" error={errors.suburb}>
          <input
            id="qf-suburb"
            data-testid="quote-suburb"
            type="text"
            autoComplete="postal-code"
            className={inputCls}
            value={form.suburb}
            onChange={handle("suburb")}
            placeholder="Brisbane 4000"
            required
          />
        </Field>
      </div>

      <div className="mt-4">
        <Field
          label="I'm interested in"
          htmlFor="qf-interest"
          error={errors.interest}
        >
          <select
            id="qf-interest"
            data-testid="quote-interest"
            className={selectCls}
            value={form.interest}
            onChange={handle("interest")}
            required
          >
            {INTERESTS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </Field>
      </div>

      <div className="mt-4">
        <Field
          label="Brief message"
          htmlFor="qf-message"
          error={errors.message}
          optional
        >
          <textarea
            id="qf-message"
            data-testid="quote-message"
            rows={3}
            className={textareaCls}
            value={form.message}
            onChange={handle("message")}
            placeholder="Anything you'd like the team to know about your roof, bills or timeline."
          />
        </Field>
      </div>

      {/* Honeypot — hidden from real users, must remain blank. */}
      <div
        className="absolute left-[-9999px] w-px h-px overflow-hidden"
        aria-hidden="true"
      >
        <label htmlFor="qf-website">Website</label>
        <input
          id="qf-website"
          tabIndex={-1}
          autoComplete="off"
          type="text"
          value={form.website}
          onChange={handle("website")}
        />
      </div>

      {errors._form && (
        <div
          className="mt-4 text-[13px] text-red-600"
          data-testid="quote-form-error"
        >
          {errors._form}
        </div>
      )}

      <div className="mt-6">
        <QButton
          type="submit"
          variant="primary"
          size="lg"
          iconRight={ArrowRight}
          disabled={submitting}
          className="w-full"
          data-testid="quote-submit"
        >
          {submitting ? "Sending…" : "Get my free quote"}
        </QButton>
      </div>

      <div className="mt-4 flex items-start gap-2 text-[12.5px] text-subtle leading-relaxed">
        <Lock className="h-3.5 w-3.5 mt-0.5 shrink-0" strokeWidth={1.75} />
        <span>
          By submitting, you agree we may contact you about your enquiry. We
          never share your details.
        </span>
      </div>
    </form>
  );
};

export default QuoteForm;
